import React, { useContext, useMemo, useState } from 'react'
import useNetTradeCell from '../../../tablecells/NetTrade'
import { TableVirtuoso } from 'react-virtuoso'
import { Box, Checkbox, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, } from '@mui/material';
import { ThemeContext } from '../../../../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import NoDataTable from '../../../table/NoDataTable'
import CheckIcon from "@mui/icons-material/Check"
import { ArrowRight } from "@mui/icons-material";
import DeleteConfirmationModal from './DeletConfirmationModal';
import { OneClickTrading } from '../../../../api/request/dashboard/dashboard';
import toast from 'react-hot-toast';
import { calculateGroupPL, getGroupCommission, getGroupInterest, getGroupNetOpenValue, getGroupNetValue } from './utils/groupCalculation';

export default function NetTrade() {
    const { t } = useTranslation()
    const { darkMode } = useContext(ThemeContext)
    const columns = useNetTradeCell();
    const positions = useSelector((state) => state.orderPosition.positions, shallowEqual);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [contextMenu, setContextMenu] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState(Object.fromEntries(columns.map(col => [col.title, true])));
    const [submenuAnchor, setSubmenuAnchor] = useState(null);
    const imgSrc = darkMode ? '/hs-white.png' : '/hs-logo.png'
    const columnsToRender = useMemo(() => columns.filter(col => visibleColumns[col.title]), [visibleColumns, columns]);
    const [activeRowId, setActiveRowId] = useState(null);
    const [activeContextRow, setActiveContextRow] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedRows, setSelectedRows] = useState([])
    const { accountId: accountID } = useSelector(state => state.accountSummary);
    const symbol = useSelector(state => state.watchlist.symbols)
    const symbolParam = useSelector(state => state.symbol.allSymbolParams, shallowEqual);
    const refSymbol = useSelector(state => state.updateSymbol.symbols?.[symbol?.referenceSymbolId]);
    // const updateSymbol= useSelector(state => state.updateSymbol.symbols)
    const toggleColumnVisibility = (title) => {
        setVisibleColumns(prev => ({ ...prev, [title]: !prev[title] }));
    };
    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc',
                };
            }
            return { key, direction: 'asc' };
        });
    };
    const groupedPositions = useMemo(() => {
        const nhMap = {};

        positions?.forEach((pos) => {
            const symbolKey = pos.symbolId;
            if (!nhMap[symbolKey]) {
                nhMap[symbolKey] = { buy: 0, sell: 0 };
            }
            if (pos.side === 1) nhMap[symbolKey].buy += pos.amount;
            else if (pos.side === -1) nhMap[symbolKey].sell += pos.amount;
        });

        const groups = {};
        positions?.forEach((pos) => {
            const key = `${pos.symbolId}_${pos.side}`;
            if (!groups[key]) {
                groups[key] = {
                    ...pos,
                    amount: 0,
                    openPriceNumerator: 0,
                    openPriceDenominator: 0,
                    positionList: [],
                    symbolName: pos.symbolName,
                };
            }

            groups[key].amount += pos.amount;
            groups[key].openPriceNumerator += pos.amount * pos.openPrice;
            groups[key].openPriceDenominator += pos.amount;
            groups[key].positionList.push(pos);

            const { buy, sell } = nhMap[pos.symbolId];
            const hasBothSides = buy > 0 && sell > 0;

            if (hasBothSides) {
                const diff = buy - sell;
                if (diff > 0 && pos.side === 1) {
                    groups[key].nh = diff; // More Buy → show +NH in Buy row
                } else if (diff < 0 && pos.side === -1) {
                    groups[key].nh = diff; // More Sell → show -NH in Sell row
                }
            }


        });

        return Object.values(groups).map(group => ({
            ...group,
            openPrice: group.openPriceNumerator / group.openPriceDenominator,
            netOpenValue: getGroupNetOpenValue(group.positionList, symbol?.[group.symbolId]?.contractSize),
            commission: getGroupCommission({ positionList: group.positionList, symbol: symbol?.[group.symbolId], symbolParam: symbolParam?.[group.symbolId], refSymbol, side: group.side }),
            interest: getGroupInterest(group.positionList),
            netValue: getGroupNetValue(group.positionList, symbol?.[group.symbolId], group.side),
            profit: calculateGroupPL({ positionList: group.positionList, symbolId: group.symbolId, side: group.side,symbols : symbol, allSymbolParams:symbolParam})
        }));
    }, [positions]);

    const sortedData = useMemo(() => {
        if (!Array.isArray(groupedPositions)) return [];

        return [...groupedPositions].sort((a, b) => {
            if (!sortConfig.key) return 0;
            const valueA = a[sortConfig.key];
            const valueB = b[sortConfig.key];

            if (typeof valueA === 'number' && typeof valueB === 'number') {
                return sortConfig.direction === 'asc' ? valueA - valueB : valueB - valueA;
            }
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return sortConfig.direction === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }
            return 0;
        });
    }, [groupedPositions, sortConfig]);

    const filteredData = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();

        if (!query) return sortedData;

        return sortedData.filter((row) =>
            columns.some((col) => {
                const value = row[col.dataIndex];
                return value?.toString().toLowerCase().includes(query);
            })
        );
    }, [sortedData, columns, searchQuery]);

    const handleContextMenu = (event, item) => {
        event.preventDefault();
        setActiveContextRow(item);
        setContextMenu({
            mouseX: event.clientX,
            mouseY: event.clientY,
        });
    };
    const handleDelete = () => {
        setDeleteConfirmOpen(true);
        setContextMenu(null);
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
    };

    const handleHedgePosition = async () => {
        const symbolId = activeContextRow.symbolId;
        let nh = typeof activeContextRow.nh === 'number' ? activeContextRow.nh : null;

        if (nh === null) {
            const sameSymbolPositions = filteredData.filter(p => p.symbolId === symbolId);

            const totalBuy = sameSymbolPositions
                .filter(p => p.side === 1)
                .reduce((sum, p) => sum + p.amount, 0);

            const totalSell = sameSymbolPositions
                .filter(p => p.side === -1)
                .reduce((sum, p) => sum + p.amount, 0);

            nh = totalBuy - totalSell;
        }

        if (Math.abs(nh) === 0) {
            toast.success("The net hedge is 0.");
            setContextMenu(null);
            return;
        }

        const currentSide = activeContextRow.side;
        const oppositeSide = currentSide === 1 ? -1 : 1;

        const oppositePosition = filteredData.find(
            (p) => p.symbolId === symbolId && p.side === oppositeSide
        );

        let hedgeSide;
        let hedgeAmount;
        let orderComment = "Hedge Net Trade";

        if (!oppositePosition) {
            hedgeSide = oppositeSide;
            hedgeAmount = parseFloat(activeContextRow.amount.toFixed(3));
            orderComment = "Hedge Created (no opposite position)";
        } else {
            hedgeSide = activeContextRow.amount < oppositePosition.amount
                ? activeContextRow.side
                : oppositePosition.side;

            hedgeAmount = parseFloat(Math.abs(nh).toFixed(3));
        }

        const orderPayload = {
            accountId: Number(accountID),
            symbolId,
            amount: hedgeAmount,
            side: hedgeSide,
            comment: orderComment,
        };

        try {
            const response = await OneClickTrading(orderPayload);
            const symbolData = symbol[symbolId];

            // toast.success(
            //     `Trade opened: ID #${response.data.id}, ${symbolData?.name || "Symbol"} at ${response.data.price}, Amount: ${response.data.amount}`
            // );
            console.log("Hedge trade successful:", response);
        } catch (error) {
            console.error("Hedge trade failed:", error);
            toast.error("Failed to place hedge trade.");
        }

        setContextMenu(null);
    };

    const Row = useMemo(() => (index, item) => {
        const isActive = activeRowId === item.id && selectedRows.length === 0;
        const backgroundColor = isActive ? (darkMode ? 'var(--highlightDark)' : 'var(--highlightLight)') : (darkMode ? '#151924' : '#fff');

        return (
            <>
                {columnsToRender.map((col, colIndex) => {
                    const isSymbolCol = col.dataIndex === 'symbolName';

                    return (
                        <TableCell key={colIndex} onClick={() => setActiveRowId(item.id)} sx={{ backgroundColor, '&:hover .virtual-cell': { backgroundColor: darkMode ? '#1f2635' : '#f5f5f5', }, fontSize: "0.8rem", border: "none", padding: "5px", }}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                handleContextMenu(event, item);
                                setActiveRowId(item.id);
                            }}>
                            {isSymbolCol ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox checked={selectedRows.includes(item)} size="small" sx={{ padding: 0 }} onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedRows(prev => [...prev, item]);
                                                setActiveRowId(null); // disable single-select highlight
                                            } else {
                                                setSelectedRows(prev => prev.filter(row => row !== item));
                                            }
                                        }} />
                                    {col.render ? col.render(item[col.dataIndex], item) : item[col.dataIndex] ?? "--"}
                                </Box>
                            ) : (
                                col.render ? col.render(item[col.dataIndex], item) : item[col.dataIndex] ?? "--"
                            )}
                        </TableCell>
                    );
                })}
            </>
        );
    }, [columnsToRender, darkMode, selectedRows, activeRowId]);

    const fixedHeaderContent = () => {
        return (
            <TableRow sx={{ borderTop: `1px solid ${darkMode ? "var(--borderDark)" : "var(--borderLight)"}` }}>
                {columnsToRender.map((col, index) => (
                    <TableCell onContextMenu={(event) => handleContextMenu(event, col)} style={{ position: "sticky", top: 0, zIndex: 2, width: col?.width, fontWeight: 550, textTransform: "capitalize" }} key={index} sx={{ fontSize: "0.85rem", fontWeight: 200, p: 1, backgroundColor: darkMode ? "var(--background-color)" : "var(--white)", cursor: "pointer", borderBottom: "1px solid rgba(224, 224, 224, 0.3)", borderRight: `1px solid ${darkMode ? "#444" : "var(--borderLight)"}`, }} onClick={() => handleSort(col.dataIndex)}>
                        <TableSortLabel active={sortConfig.key === col.dataIndex} direction={sortConfig.direction}>
                            {col.title}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>);
    }

    const VirtuosoTableComponents = {
        Table: (props) => <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} />,
        TableHead: React.forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
        TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
    };
    return (
        <>
            <Paper style={{ width: "100%", height: "calc(100% - 35px)", backgroundColor: darkMode ? "#151924" : "#fff", }}>
                {filteredData.length === 0 ? (<NoDataTable columns={columns} data={filteredData} noDataMessage={t('noData')} />
                ) : (
                    <Box sx={{ backgroundColor: darkMode ? "#151924" : "#fff", height: '100%' }}>
                        <TableVirtuoso data={filteredData} fixedHeaderContent={fixedHeaderContent} itemContent={Row} components={VirtuosoTableComponents} />
                    </Box>
                )}
            </Paper>
            <div style={{ height: 35 }}>
                <div className='flex items-center px-1 justify-between'>
                    <TextField size="small" variant="outlined" placeholder={t("mobSearch")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: '250px', mx: 1, mt: 0.3 }} InputProps={{ sx: { fontSize: 13, padding: 0, height: 30, "& input": { padding: "0", paddingLeft: 1, height: "30px", }, }, }} />
                    <div className='flex items-center opacity-50'>
                        <small>{t('poweredby')} </small><a href="https://hybridsolutions.com/" target='_blank'><img src={imgSrc} alt="hs" style={{ height: darkMode ? 40 : 20 }} className='ms-2' />
                        </a>
                    </div>
                </div>
            </div>


            <Menu sx={{ "& .MuiList-root": { padding: 0 } }} open={contextMenu !== null} onClose={() => { setContextMenu(null); setSubmenuAnchor(null); }} anchorReference="anchorPosition" anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
                <div style={{ backgroundColor: darkMode ? "#262f3f" : "" }}>
                    <MenuItem sx={{ fontSize: 13.5 }} onClick={handleDelete}>{t("delete")}</MenuItem>
                    <MenuItem sx={{ fontSize: 13.5 }} onClick={handleHedgePosition}>{t("hedgePosition")}</MenuItem>
                    <MenuItem sx={{ fontSize: 13.5 }} onClick={(e) => setSubmenuAnchor(e.currentTarget)}>{t('columns')} <ArrowRight /></MenuItem>
                </div>
            </Menu>

            <Menu open={Boolean(submenuAnchor)} anchorEl={submenuAnchor} onClose={() => { setSubmenuAnchor(null); setContextMenu(null); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} sx={{ "& .MuiList-root": { padding: 0 } }}>
                {columns.map((col, index) => (
                    <MenuItem key={index} onClick={col.title === t('symbol') ? undefined : () => toggleColumnVisibility(col.title)} disabled={col.title === t('symbol')}
                        sx={{ fontSize: 13.5, display: "flex", justifyContent: "space-between", alignItems: "center", opacity: col.title === t('symbol') ? 0.6 : 1 }}>
                        {col.title} {visibleColumns[col.title] && (<CheckIcon sx={{ fontSize: 18, color: "#4597ff" }} />)}
                    </MenuItem>
                ))}
            </Menu>

            <DeleteConfirmationModal open={deleteConfirmOpen} onCancel={handleCancelDelete} selectedRows={selectedRows} activeContextRow={activeContextRow} activeRowId={activeRowId} filteredData={filteredData} setDeleteConfirmOpen={setDeleteConfirmOpen} setActiveContextRow={setActiveContextRow} setSelectedRows={setSelectedRows} setActiveRowId={setActiveRowId} />
        </>
    )
}
